import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Download, Wand2, Image as ImageIcon, X } from 'lucide-react';
import { mockGeneratedImages } from '../mock';
import Header from '../components/Header';


const AIImage = () => {
  const [activeTab, setActiveTab] = useState('text-to-image');
  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState('square');
  const [quality] = useState('hd');
  const [style] = useState('vivid');
  const [loading, setLoading] = useState(false);
  const [generatedImages, setGeneratedImages] = useState(mockGeneratedImages);
  const [uploadedImage, setUploadedImage] = useState(null);

  useEffect(() => {
    const savedImages = localStorage.getItem('pulseChat_generatedImages');
    if (savedImages) {
      try {
        const parsedImages = JSON.parse(savedImages);
        const combined = [...parsedImages, ...mockGeneratedImages];
        const unique = combined.filter((img, index, self) =>
          index === self.findIndex((t) => t.id === img.id)
        );
        setGeneratedImages(unique);
      } catch {
        setGeneratedImages(mockGeneratedImages);
      }
    }
  }, []);

  useEffect(() => {
    const userImages = generatedImages.filter(img =>
      !mockGeneratedImages.some(mock => mock.id === img.id)
    );
    if (userImages.length > 0) {
      localStorage.setItem('pulseChat_generatedImages', JSON.stringify(userImages));
    }
  }, [generatedImages]);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Prompt required", { description: "Please enter a description" });
      return;
    }
    if (activeTab === 'image-to-image' && !uploadedImage) {
      toast.error("Image required", { description: "Please upload an image first" });
      return;
    }

    setLoading(true);
    try {
      const endpoint = activeTab === 'image-to-image'
        ? 'https://pulse-chat.pro/image/api/generate/image-to-image'
        : 'https://pulse-chat.pro/image/api/generate/text-to-image';

      const requestBody = activeTab === 'image-to-image'
        ? { prompt, image: uploadedImage }
        : { prompt, size, quality, style };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to generate image');

      if (data.image_url) {
        const newImage = {
          id: Date.now(),
          prompt,
          imageUrl: data.image_url,
          timestamp: new Date().toISOString(),
          size,
          type: activeTab
        };
        setGeneratedImages(prev => [newImage, ...prev]);
        toast.success("Image generated!", { description: "Your image is ready" });
        setPrompt('');
        if (activeTab === 'image-to-image') setUploadedImage(null);
      } else {
        throw new Error(data.error || 'Generation failed');
      }
    } catch (error) {
      toast.error("Generation failed", { description: error.message || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (imageUrl, promptText, e) => {
    e.stopPropagation();
    try {
      toast.info("Downloading...", { description: "Please wait" });
      const filename = `pulsechat_${promptText.substring(0, 30).replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.png`;
      try {
        const res = await fetch(imageUrl);
        if (res.ok) {
          const blob = await res.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = filename;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(blobUrl); }, 100);
          toast.success("Downloaded!", { description: "Check your downloads folder" });
          return;
        }
      } catch {
        console.log('Direct fetch failed, trying proxy...');
      }
      const proxyUrl = `https://pulse-chat.pro/image/api/download?url=${encodeURIComponent(imageUrl)}&filename=${encodeURIComponent(filename)}`;
      const a = document.createElement('a');
      a.href = proxyUrl;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => document.body.removeChild(a), 100);
      toast.success("Downloaded!", { description: "Check your downloads folder" });
    } catch {
      toast.error("Download failed", { description: "Please try again" });
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setUploadedImage(ev.target?.result);
      reader.readAsDataURL(file);
    }
  };

  const clearHistory = () => {
    if (window.confirm('Clear all generated images?')) {
      setGeneratedImages(mockGeneratedImages);
      localStorage.removeItem('pulseChat_generatedImages');
      toast.success("History cleared");
    }
  };

  return (
    <>
    <Header />
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="pt-24 px-6 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-4">AI Image Generation</h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">Create stunning images from text or transform existing images with AI</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); setUploadedImage(null); setPrompt(''); }} className="w-full">
                <TabsList className="grid w-full grid-cols-2 bg-white/5 border border-white/10">
                  <TabsTrigger value="text-to-image" className="data-[state=active]:bg-yellow-400 data-[state=active]:text-black">Text to Image</TabsTrigger>
                  <TabsTrigger value="image-to-image" className="data-[state=active]:bg-yellow-400 data-[state=active]:text-black">Image to Image</TabsTrigger>
                </TabsList>

                <TabsContent value="text-to-image" className="space-y-4 mt-6">
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">Describe your image</label>
                    <Textarea
                      placeholder="A serene mountain landscape at sunset with golden light..."
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      className="min-h-[120px] bg-white/5 border-white/10 text-white placeholder:text-white/40 resize-none"
                    />
                  </div>
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">Image Size</label>
                    <Select value={size} onValueChange={setSize}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/10">
                        <SelectItem value="square">Square (1024x1024)</SelectItem>
                        <SelectItem value="portrait">Portrait (1024x1792)</SelectItem>
                        <SelectItem value="landscape">Landscape (1792x1024)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TabsContent>

                <TabsContent value="image-to-image" className="space-y-4 mt-6">
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">Upload Image</label>
                    {!uploadedImage ? (
                      <div>
                        <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" id="image-upload" />
                        <label htmlFor="image-upload" className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-yellow-400/50 transition-colors bg-white/5">
                          <ImageIcon className="w-12 h-12 text-white/40 mb-2" />
                          <span className="text-white/60 text-sm">Click to upload image</span>
                          <span className="text-white/40 text-xs mt-1">PNG, JPG supported</span>
                        </label>
                      </div>
                    ) : (
                      <div className="relative rounded-lg overflow-hidden bg-white/5 border border-white/10">
                        <button onClick={() => setUploadedImage(null)} className="absolute top-2 right-2 z-10 bg-black/60 hover:bg-black/80 text-white rounded-full p-1">
                          <X className="w-4 h-4" />
                        </button>
                        <img src={uploadedImage} alt="Uploaded" className="w-full h-auto object-contain" />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">Transformation prompt</label>
                    <Textarea
                      placeholder="Transform this into a watercolor painting style..."
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      className="min-h-[100px] bg-white/5 border-white/10 text-white placeholder:text-white/40 resize-none"
                    />
                  </div>
                </TabsContent>
              </Tabs>

              <Button onClick={handleGenerate} disabled={loading} className="w-full bg-[#F4D03F] hover:bg-[#e5c230] text-black font-semibold py-6 rounded-lg transition-all duration-300 hover:scale-[1.02]">
                {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generating...</> : <><Wand2 className="w-5 h-5 mr-2" />Generate Image</>}
              </Button>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-white text-lg font-semibold">Generated Images</h3>
                {generatedImages.length > mockGeneratedImages.length && (
                  <Button onClick={clearHistory} variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/5">Clear All</Button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4 max-h-[600px] overflow-y-auto">
                {generatedImages.map((image) => (
                  <Card key={image.id} className="group relative bg-white/5 border-white/10 overflow-hidden cursor-pointer hover:border-yellow-400/50 transition-all duration-300">
                    <img src={image.imageUrl} alt={image.prompt} className="w-full h-48 object-cover" />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Button size="sm" onClick={(e) => handleDownload(image.imageUrl, image.prompt, e)} className="bg-[#F4D03F] hover:bg-[#e5c230] text-black">
                        <Download className="w-4 h-4 mr-2" />Download
                      </Button>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                      <p className="text-white text-xs truncate">{image.prompt}</p>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default AIImage;